<script setup>
import { ref, computed, onMounted } from 'vue';

const props = defineProps({
    designReferencesUrl: { type: String, required: true },
    flowieHandoff: { type: Object, default: null },
});

const emit = defineEmits(['complete']);

// Prefilled from Flowie when the user arrived here from an action plan. The
// page itself already exists at this point; nothing is generated until the
// user submits this form.
const handoff = props.flowieHandoff ?? {};

const tone = ref(handoff.tone || 'professional');
const audience = ref(handoff.audience || '');
const topic = ref(handoff.topic || '');
const cta = ref(handoff.cta || '');
const language = ref(handoff.language || '');
const designReferenceContentId = ref(null);
const designReferences = ref([]);
const loading = ref(false);
const showFlowieNote = ref(Boolean(props.flowieHandoff));

const toneOptions = [
    { value: 'minimal', labelKey: 'page:aiPageBuilder.onboarding.tone.minimal' },
    { value: 'professional', labelKey: 'page:aiPageBuilder.onboarding.tone.professional' },
    { value: 'provoking', labelKey: 'page:aiPageBuilder.onboarding.tone.provoking' },
];

const canSubmit = computed(() => {
    return topic.value.trim().length > 0 && !loading.value;
});

const loadDesignReferences = async () => {
    try {
        const res = await axios.get(props.designReferencesUrl);
        designReferences.value = res.data.pages || [];
    } catch (e) {
        // Silently fail
    }
};

const handleSubmit = () => {
    if (!canSubmit.value) return;
    loading.value = true;

    emit('complete', {
        tone: tone.value,
        audience: audience.value.trim() || null,
        topic: topic.value.trim(),
        cta: cta.value.trim() || null,
        enable_interactive_features: true,
        language: language.value.trim() || null,
        design_reference_content_id: designReferenceContentId.value || null,
    });
};

const resetLoading = () => {
    loading.value = false;
};

defineExpose({ resetLoading });

onMounted(() => {
    loadDesignReferences();
});
</script>

<template>
    <div class="flex flex-col h-full">
        <!-- Scrollable form area -->
        <div class="flex-1 min-h-0 overflow-y-auto p-5 pb-2">
            <div class="mb-6">
                <h2 class="text-lg font-semibold text-gray-900">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.title"></mf-translate>
                </h2>
                <p class="text-sm text-gray-500 mt-1">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.subtitle"></mf-translate>
                </p>
            </div>

            <flowie-context-note
                v-if="showFlowieNote"
                :dismissible="false"
            />

            <!-- Topic (required, first) -->
            <div class="mb-5 relative">
                <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.topicLabel"></mf-translate>
                    <span class="text-red-500">*</span>
                </label>
                <input-textarea
                    v-model:content="topic"
                    :placeholder="_mft('page:aiPageBuilder.onboarding.topicPlaceholder')"
                    :resizable="false"
                    input-height-class="h-24"
                    maxlength="1000"
                    autofocus
                ></input-textarea>
                <span class="absolute right-2 bottom-2 pointer-events-none text-xxs opacity-50">
                    {{ 1000 - topic.length }}
                </span>
            </div>

            <!-- Audience -->
            <div class="mb-5 relative">
                <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.audienceLabel"></mf-translate>
                </label>
                <input-textarea
                    v-model:content="audience"
                    :resizable="false"
                    input-height-class="h-20"
                    :placeholder="_mft('page:aiPageBuilder.onboarding.audiencePlaceholder')"
                    maxlength="1000"
                ></input-textarea>
                <span class="absolute right-2 bottom-2 pointer-events-none text-xxs opacity-50">
                    {{ 1000 - audience.length }}
                </span>
            </div>

            <!-- Call to action -->
            <div class="mb-5 relative">
                <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.ctaLabel"></mf-translate>
                </label>
                <input-textarea
                    v-model:content="cta"
                    :placeholder="_mft('page:aiPageBuilder.onboarding.ctaPlaceholder')"
                    :resizable="false"
                    input-height-class="h-24"
                    maxlength="1000"
                ></input-textarea>
                <span class="absolute right-2 bottom-2 pointer-events-none text-xxs opacity-50">
                    {{ 1000 - cta.length }}
                </span>
            </div>

            <!-- Tone -->
            <div class="mb-5">
                <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.toneLabel"></mf-translate>
                </label>
                <div class="flex gap-2">
                    <component
                        v-for="option in toneOptions"
                        :key="option.value"
                        :is="tone === option.value ? 'button-primary' : 'button-default'"
                        @click="tone = option.value"
                        type="button"
                        size="md"
                        class="flex-1 justify-center"
                    >
                        <mf-translate :domainkey="option.labelKey"></mf-translate>
                    </component>
                </div>
            </div>

            <!-- Language -->
            <div class="mb-5">
                <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.languageLabel"></mf-translate>
                </label>
                <input-text
                    v-model:content="language"
                    :placeholder="_mft('page:aiPageBuilder.onboarding.languagePlaceholder')"
                />
            </div>

            <!-- Design reference -->
            <div v-if="designReferences.length > 0">
                <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.designReference"></mf-translate>
                </label>
                <select
                    v-model="designReferenceContentId"
                    class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                >
                    <option :value="null">
                        <mf-translate domainkey="page:aiPageBuilder.onboarding.noReference"></mf-translate>
                    </option>
                    <option
                        v-for="page in designReferences"
                        :key="page.block_editor_content_id"
                        :value="page.block_editor_content_id"
                    >
                        {{ page.title }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Sticky submit button -->
        <div class="flex-shrink-0 p-5 pt-3 border-t border-gray-200 bg-white">
            <button-base
                @click="handleSubmit"
                style-type="primary"
                type="button"
                :disabled="!canSubmit"
                class="py-2 w-full justify-center"
            >
                <template #icon v-if="loading">
                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                </template>
                <mf-translate :domainkey="loading ? 'page:aiPageBuilder.generating' : 'page:aiPageBuilder.onboarding.startBuilding'"></mf-translate>
            </button-base>
        </div>
    </div>
</template>
